import { z } from "zod";
import { isValidPhoneNumber, CountryCode } from "libphonenumber-js";
import _dialCodesJson from "~/dial_codes.json";

export type VALID_SCHEMAS_KEYS =
  | "REQUIRED"
  | "DEFAULT"
  | "NAME"
  | "EMAIL"
  | "PASS"
  | "MOBILE"
  | "MOBILE_ASYNC"
  | "NUMBER"
  | "CHECKBOX"
  | "IMAGE"
  | "CARD_NUMBER"
  | "EXPIRY_DATE"
  | "CARD_SECURITY_CODE"
  | "POST_CODE";

const dialCodesJson = _dialCodesJson as Record<string, CountryCode>;
const fileSizeLimit = 5 * 1024 * 1024; // 5MB

const requiredMessage = (label: string) => `${label} is required`;
const invalidMessage = (label: string) => `${label} is invalid`;

const VALID_SCHEMAS: Record<VALID_SCHEMAS_KEYS, z.ZodType> = {
  ["REQUIRED"]: z.any().refine((value) => !!value, {
    message: "This field is required.",
  }),
  ["DEFAULT"]: z.optional(z.any()),
  ["POST_CODE"]: z
    .string()
    .min(4, "Post code must contain at least 4 character(s)")
    .max(10, "Post code must contain at most 10 character(s)"),
  ["NAME"]: z.string().min(1, {
    message: requiredMessage("Field"),
  }),
  ["PASS"]: z
    .string()
    .min(6, {
      message: "Password must contain at least 6 characters",
    })
    .refine((value) => /[a-z]/.test(value), {
      message: "Password must contain at least one lowercase letter",
    })
    .refine((value) => /[A-Z]/.test(value), {
      message: "Password must contain at least one capital letter",
    })
    .refine((value) => /\d/.test(value), {
      message: "Password must contain at least one number",
    }),
  ["NUMBER"]: z
    .string()
    .min(1, {
      message: "Number must be at least 1 character",
    })
    .max(5, {
      message: "Number must be at most 5 characters",
    }),
  ["EMAIL"]: z
    .string({
      message: requiredMessage("Field"),
    })
    .min(1, {
      message: requiredMessage("Email"),
    })
    .email({
      message: invalidMessage("Email"),
    }),
  ["MOBILE"]: z
    .object({
      country_code: z.string().nonempty({
        message: requiredMessage("Country code"),
      }),
      number: z.string().nonempty({
        message: requiredMessage("Phone number"),
      }),
    })
    .refine(
      (value) => {
        try {
          return isValidPhoneNumber(
            value.number,
            dialCodesJson[value.country_code],
          );
        } catch {
          return false;
        }
      },
      { message: "Please enter a valid phone number" },
    ),
  ["MOBILE_ASYNC"]: z.optional(
    z
      .object({
        country_code: z.string(),
        number: z.string(),
      })
      .refine(
        (value) => {
          try {
            return isValidPhoneNumber(
              value.number,
              dialCodesJson[value.country_code],
            );
          } catch {
            return false;
          }
        },
        { message: "Please enter a valid phone number" },
      ),
  ),
  ["CHECKBOX"]: z.literal<boolean>(true, {
    errorMap: () => ({ message: requiredMessage("Checkbox") }),
  }),
  ["CARD_NUMBER"]: z.string().regex(/^\d{13,22}$/, {
    message: "Card number must be between 13 and 19 digits",
  }),
  ["EXPIRY_DATE"]: z.string().regex(/^(0[1-9]|1[012])\/(2[6-9]|[3-9]\d)$/, {
    message: "Expiry date must be valid",
  }),
  ["CARD_SECURITY_CODE"]: z.string().regex(/^\d{3,4}$/, {
    message: "CVV must be 3 or 4 digits",
  }),
  ["IMAGE"]:
    typeof File === "undefined"
      ? z.any()
      : z
          .instanceof(File, { message: requiredMessage("Image") })
          .refine(
            (file) =>
              [
                "image/png",
                "image/jpeg",
                "image/jpg",
                "image/svg+xml",
                "image/gif",
              ].includes(file.type),
            { message: "Invalid image file type" },
          )
          .refine((file) => file.size <= fileSizeLimit, {
            message: "File size should not exceed 5MB",
          }),
};

export default VALID_SCHEMAS;
